import { createReducer } from '@reduxjs/toolkit';
import {
  fetchQueues,
  savePriorityOrder,
  reorderQueues,
  revertOrder,
  setQueueCoderCount,
  QueueItem
} from '../actions/queue-priority-order.action';

export interface QueuePriorityOrderState {
  queues: QueueItem[];
  originalOrder: string[];
  currentOrder: string[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;
}

const initialState: QueuePriorityOrderState = {
  queues: [],
  originalOrder: [],
  currentOrder: [],
  isLoading: false,
  isSaving: false,
  error: null,
  saveError: null
};

const reducer = createReducer(initialState, builder => {
  builder.addCase(fetchQueues.pending, state => {
    state.isLoading = true;
    state.error = null;
  });

  builder.addCase(fetchQueues.fulfilled, (state, action) => {
    const queues = Array.isArray(action.payload) ? action.payload : [];
    const ids = queues.map(q => q.id);
    state.queues = queues;
    state.originalOrder = ids;
    state.currentOrder = ids;
    state.isLoading = false;
  });

  builder.addCase(fetchQueues.rejected, (state, action) => {
    state.isLoading = false;
    state.error = action.error.message ?? 'Failed to load queues';
  });

  builder.addCase(reorderQueues, (state, action) => {
    const { fromIndex, toIndex } = action.payload;
    const len = state.currentOrder.length;
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= len ||
      toIndex >= len ||
      fromIndex === toIndex
    ) {
      return;
    }
    const newOrder = [...state.currentOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    state.currentOrder = newOrder;
  });

  builder.addCase(revertOrder, state => {
    state.currentOrder = [...state.originalOrder];
  });

  builder.addCase(setQueueCoderCount, (state, action) => {
    const { queueId, coderCount } = action.payload;
    const queue = state.queues.find(q => q.id === queueId);
    if (queue) {
      queue.coderCount = coderCount;
    }
  });

  builder.addCase(savePriorityOrder.pending, state => {
    state.isSaving = true;
    state.saveError = null;
  });

  builder.addCase(savePriorityOrder.fulfilled, (state, action) => {
    state.isSaving = false;
    state.originalOrder = action.payload;
  });

  builder.addCase(savePriorityOrder.rejected, (state, action) => {
    state.isSaving = false;
    state.saveError = action.error.message ?? 'Failed to save priority order';
  });
});

export const selectHasUnsavedPriorityOrder = (state: any): boolean => {
  const slice: QueuePriorityOrderState | undefined = state?.queuePriorityOrder;
  if (!slice) return false;
  return (
    (slice.currentOrder ?? []).join(',') !== (slice.originalOrder ?? []).join(',')
  );
};

export default reducer;
